import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  Stethoscope,
  Sparkles,
} from "lucide-react";

const SUFFICIENCY_STYLES = {
  sufficient: {
    icon: CheckCircle2,
    color: "text-emerald-600",
    bg: "bg-emerald-500/5",
    border: "border-emerald-500/30",
    label: "Sufficient signal",
  },
  thin: {
    icon: HelpCircle,
    color: "text-amber-600",
    bg: "bg-amber-500/5",
    border: "border-amber-500/30",
    label: "Thin signal",
  },
  insufficient: {
    icon: AlertTriangle,
    color: "text-rose-600",
    bg: "bg-rose-500/5",
    border: "border-rose-500/30",
    label: "Insufficient signal",
  },
};

export default function TriagePanel({ triage }) {
  if (!triage) return null;
  const suff = SUFFICIENCY_STYLES[triage.information_sufficiency] || SUFFICIENCY_STYLES.thin;
  const SuffIcon = suff.icon;
  const conf = Math.round((triage.likely_incident_type_confidence || 0) * 100);
  return (
    <div
      data-testid="triage-panel"
      className="cre-surface rounded-sm p-5 md:p-6"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="cre-overline text-sky-700">Triage assessment</div>
          <h3 className="font-heading text-lg font-bold tracking-tight mt-1">
            Pre-dispatch triage
          </h3>
          <p
            data-testid="triage-summary"
            className="mt-1 text-xs text-slate-600 max-w-2xl"
          >
            {triage.summary}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          {triage.major_incident_candidate ? (
            <Badge
              data-testid="triage-major-incident-badge"
              variant="outline"
              className="rounded-sm border-rose-500/40 text-rose-700 bg-rose-500/5 font-mono uppercase text-[10px]"
            >
              <Sparkles className="h-3 w-3 mr-1" /> Major-incident candidate
            </Badge>
          ) : null}
          {triage.duplicate_candidate ? (
            <Badge
              data-testid="triage-duplicate-badge"
              variant="outline"
              className="rounded-sm border-amber-500/40 text-amber-700 bg-amber-500/5 font-mono uppercase text-[10px]"
            >
              Duplicate candidate
            </Badge>
          ) : null}
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div
          data-testid="triage-incident-type"
          className="border border-slate-200 rounded-sm p-3 bg-slate-50/60"
        >
          <div className="cre-overline text-slate-500">Likely incident type</div>
          <div className="mt-1 text-sm font-semibold text-slate-800 capitalize">
            {(triage.likely_incident_type || "uncategorised").replace(/_/g, " ")}
          </div>
          <div className="mt-1 text-[11px] font-mono text-slate-500">
            confidence {conf}% · rule-based
          </div>
        </div>

        <div
          className={`border rounded-sm p-3 ${suff.border} ${suff.bg}`}
          data-testid="triage-sufficiency"
        >
          <div className="cre-overline text-slate-500">Information sufficiency</div>
          <div className={`mt-1 text-sm font-semibold flex items-center gap-1.5 ${suff.color}`}>
            <SuffIcon className="h-4 w-4" />
            {suff.label}
          </div>
          {triage.missing_information?.length ? (
            <div className="mt-1 text-[11px] text-slate-600">
              Missing: {triage.missing_information.slice(0, 3).join(", ")}
            </div>
          ) : null}
        </div>

        <div
          data-testid="triage-recommended-agents"
          className="border border-violet-500/30 rounded-sm p-3 bg-violet-500/5"
        >
          <div className="cre-overline text-violet-700">Recommended agents</div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {(triage.recommended_agents || []).map((a) => (
              <Badge
                key={a}
                data-testid={`triage-recommended-${a}`}
                variant="outline"
                className="rounded-sm font-mono uppercase text-[10px] border-violet-500/40 text-violet-700 bg-violet-500/10"
              >
                {a}
              </Badge>
            ))}
            {!(triage.recommended_agents || []).length ? (
              <span className="text-[11px] text-slate-500">— no narrow set; run all specialists</span>
            ) : null}
          </div>
          <div className="mt-2 text-[11px] text-slate-500">
            All specialists still run by default — this list is informational.
          </div>
        </div>
      </div>

      {triage.duplicate_candidate || triage.major_incident_reasons?.length ? (
        <div className="mt-3 space-y-1.5 text-xs text-slate-600">
          {triage.duplicate_candidate ? (
            <div className="flex items-start gap-2">
              <Stethoscope className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
              <span>
                <span className="font-medium text-amber-700">Duplicate flag (candidate only):</span>{" "}
                {triage.duplicate_note}
              </span>
            </div>
          ) : null}
          {(triage.major_incident_reasons || []).map((r, i) => (
            <div key={i} className="flex items-start gap-2">
              <AlertTriangle className="h-3.5 w-3.5 text-rose-600 shrink-0 mt-0.5" />
              <span>{r}</span>
            </div>
          ))}
        </div>
      ) : null}

      <div className="mt-3 text-[11px] font-mono text-slate-500">
        Rule-based triage · no integrated KB / ITSM lookup yet · duplicate &
        known-issue flags are candidate-only.
      </div>
    </div>
  );
}
